// PDF Export — generates Delmar DPR matching template layout
// Uses pdfmake, lazy-loaded on first export

const ORANGE = "#E8611A";
const NAVY = "#26262C";
const GRAY_BG = "#E5E5E5";
const DOC_NO = "US-OPS-FRM-007-R0  26.05.2022";

// --- Helpers ---

function sanitize(str) {
  return (str || "").replace(/[^a-zA-Z0-9_\- ]/g, "").replace(/\s+/g, "_");
}

function formatDprDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function formatTime(timestamp) {
  if (!timestamp) return "";
  const d = new Date(timestamp);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}${mm}`;
}

function shouldBold(text) {
  if (!text) return false;
  const lower = text.toLowerCase();
  return (
    (lower.includes("commence") && lower.includes("hold")) ||
    lower.includes("hold completed") ||
    lower.includes("ave winch tension") ||
    lower.includes("peak tension")
  );
}

async function imageToBase64(url) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// --- Event log merge ---

function mergeEventLog(events, detailedLog) {
  const fromEvents = (events || [])
    .filter((e) => e.startAt)
    .map((e) => ({
      timestamp: new Date(e.startAt),
      description: [e.line, e.operation, e.remarks].filter(Boolean).join(" — "),
      source: "event",
    }));

  const fromLog = (detailedLog || []).map((entry) => ({
    timestamp: new Date(entry.timestamp),
    description: entry.description,
    source: "detailed_log",
  }));

  return [...fromEvents, ...fromLog].sort(
    (a, b) => a.timestamp - b.timestamp
  );
}

// --- Table builders ---

function buildProjectInfoTable(project, dpr) {
  return {
    table: {
      widths: [100, "*", 80, "*"],
      body: [
        [
          { text: "DAILY PROGRESS REPORT", bold: true, fontSize: 12, alignment: "center", colSpan: 4 },
          {}, {}, {},
        ],
        [
          { text: "Project Title", bold: true, fontSize: 9 },
          { text: project.title || "", fontSize: 9 },
          { text: "Date d/m/y", bold: true, fontSize: 9 },
          { text: formatDprDate(dpr.reportDate), fontSize: 9 },
        ],
        [
          { text: "Project Number", bold: true, fontSize: 9 },
          { text: String(dpr.dprNumber).padStart(2, "0"), fontSize: 9 },
          { text: "Client", bold: true, fontSize: 9 },
          { text: project.customer || "", fontSize: 9 },
        ],
        [
          { text: "Vessel", bold: true, fontSize: 9 },
          { text: project.vessel || "", fontSize: 9 },
          { text: "Location", bold: true, fontSize: 9 },
          { text: project.disconnectLocation || project.connectLocation || "", fontSize: 9 },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
    },
  };
}

function buildSummaryTable(dpr) {
  return {
    table: {
      widths: [100, "*"],
      body: [
        [
          {
            text: "SUMMARY OF OVERALL PROJECT PROGRESSION AND CURRENT STATUS",
            bold: true,
            fontSize: 9,
            alignment: "center",
            colSpan: 2,
            fillColor: GRAY_BG,
          },
          {},
        ],
        [
          { text: "Last 24 Hours", bold: true, fontSize: 9 },
          { text: dpr.summaryLast24h || "", fontSize: 9 },
        ],
        [
          { text: "Next 24 Hours", bold: true, fontSize: 9 },
          { text: dpr.summaryNext24h || "", fontSize: 9 },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
    },
  };
}

function buildWeatherTable(weather) {
  const slots = ["0600", "1200", "1800", "2400"];
  const getSlot = (slot) => weather.find((w) => w.timeSlot === slot) || {};

  const headerRow1 = [
    { text: "Time", bold: true, fontSize: 8, rowSpan: 2, alignment: "center" },
    { text: "Wind", bold: true, fontSize: 8, colSpan: 3, alignment: "center" },
    {}, {},
    { text: "Sea", bold: true, fontSize: 8, colSpan: 2, alignment: "center" },
    {},
    { text: "Swell", bold: true, fontSize: 8, colSpan: 2, alignment: "center" },
    {},
    { text: "Motion", bold: true, fontSize: 8, colSpan: 2, alignment: "center" },
    {},
  ];

  const headerRow2 = [
    {},
    { text: "Speed", bold: true, fontSize: 7, alignment: "center" },
    { text: "Dir.", bold: true, fontSize: 7, alignment: "center" },
    { text: "Height", bold: true, fontSize: 7, alignment: "center" },
    { text: "Period", bold: true, fontSize: 7, alignment: "center" },
    { text: "Height", bold: true, fontSize: 7, alignment: "center" },
    { text: "Dir.", bold: true, fontSize: 7, alignment: "center" },
    { text: "Period", bold: true, fontSize: 7, alignment: "center" },
    { text: "Pitch", bold: true, fontSize: 7, alignment: "center" },
    { text: "Roll", bold: true, fontSize: 7, alignment: "center" },
  ];

  const dataRows = slots.map((slot) => {
    const w = getSlot(slot);
    const cell = (v) => ({ text: v != null && v !== "" ? String(v) : "-", fontSize: 8, alignment: "center" });
    return [
      { text: slot, bold: true, fontSize: 8, alignment: "center" },
      cell(w.windSpeed), cell(w.windDir), cell(w.windHeight),
      cell(w.seaPeriod), cell(w.seaHeight),
      cell(w.swellDir), cell(w.swellPeriod),
      cell(w.pitch), cell(w.roll),
    ];
  });

  return {
    table: {
      headerRows: 2,
      widths: [40, "*", "*", "*", "*", "*", "*", "*", "*", "*"],
      body: [
        [{ text: "WEATHER", bold: true, fontSize: 9, alignment: "center", colSpan: 10, fillColor: GRAY_BG }, {}, {}, {}, {}, {}, {}, {}, {}, {}],
        headerRow1,
        headerRow2,
        ...dataRows,
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
    },
  };
}

function buildHsseqTable(hsseq) {
  const categories = [
    { key: "incident_near_miss", label: "Incident/Near Miss" },
    { key: "environmental", label: "Environmental\nConcerns" },
    { key: "equipment_damage", label: "Equipment Damage" },
    { key: "management_of_change", label: "Management of\nChange" },
    { key: "others", label: "Others" },
  ];

  const rows = categories.map((cat) => {
    const entry = hsseq.find((h) => h.category === cat.key);
    return [
      { text: cat.label, bold: true, fontSize: 8 },
      { text: entry?.details || "N/A", fontSize: 8 },
    ];
  });

  return {
    table: {
      widths: [120, "*"],
      body: [
        [
          { text: "HSSEQ", bold: true, fontSize: 9, alignment: "center", colSpan: 2, fillColor: GRAY_BG },
          {},
        ],
        [
          { text: "Description", bold: true, fontSize: 8, fillColor: GRAY_BG },
          { text: "Details", bold: true, fontSize: 8, fillColor: GRAY_BG },
        ],
        ...rows,
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
    },
  };
}

function buildEventLogTable(mergedLog) {
  const rows = mergedLog.map((entry) => {
    const isBold = shouldBold(entry.description);
    return [
      { text: formatTime(entry.timestamp), fontSize: 8, alignment: "center", bold: isBold },
      { text: entry.description, fontSize: 8, bold: isBold },
    ];
  });

  if (!rows.length) {
    rows.push([
      { text: "", fontSize: 8 },
      { text: "No log entries", fontSize: 8, italics: true, color: "#999999" },
    ]);
  }

  return {
    table: {
      headerRows: 2,
      dontBreakRows: true,
      widths: [60, "*"],
      body: [
        [
          { text: "LOG OF EVENTS IN THE LAST 24 HOURS", bold: true, fontSize: 9, alignment: "center", colSpan: 2, fillColor: GRAY_BG },
          {},
        ],
        [
          { text: "Time", bold: true, fontSize: 8, alignment: "center", fillColor: GRAY_BG },
          { text: "Description", bold: true, fontSize: 8, fillColor: GRAY_BG },
        ],
        ...rows,
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
    },
  };
}

function buildPersonnelTable(personnel) {
  const rows = personnel.map((p) => [
    { text: p.name, fontSize: 8 },
    { text: p.position, fontSize: 8 },
    { text: p.locationToday, fontSize: 8 },
    { text: p.locationTomorrow, fontSize: 8 },
  ]);

  if (!rows.length) {
    rows.push([
      { text: "", colSpan: 4, fontSize: 8 },
      {}, {}, {},
    ]);
  }

  return {
    table: {
      headerRows: 2,
      widths: ["*", "*", "*", "*"],
      body: [
        [
          { text: "DELMAR PERSONNEL IN FIELD", bold: true, fontSize: 9, alignment: "center", colSpan: 4, fillColor: GRAY_BG },
          {}, {}, {},
        ],
        [
          { text: "Name", bold: true, fontSize: 8, fillColor: GRAY_BG },
          { text: "Position", bold: true, fontSize: 8, fillColor: GRAY_BG },
          { text: "Location (Today)", bold: true, fontSize: 8, fillColor: GRAY_BG },
          { text: "Location (Tomorrow)", bold: true, fontSize: 8, fillColor: GRAY_BG },
        ],
        ...rows,
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
    },
  };
}

function buildToolTable(tools) {
  const rows = tools.map((t) => [
    { text: t.itemId, fontSize: 8 },
    { text: t.description, fontSize: 8 },
    { text: t.vessel, fontSize: 8 },
  ]);

  if (!rows.length) {
    rows.push([
      { text: "", colSpan: 3, fontSize: 8 },
      {}, {},
    ]);
  }

  return {
    table: {
      headerRows: 2,
      widths: [100, "*", 120],
      body: [
        [
          { text: "TOOL/EQUIPMENT DAILY USAGE", bold: true, fontSize: 9, alignment: "center", colSpan: 3, fillColor: GRAY_BG },
          {}, {},
        ],
        [
          { text: "Item ID", bold: true, fontSize: 8, fillColor: GRAY_BG },
          { text: "Description", bold: true, fontSize: 8, fillColor: GRAY_BG },
          { text: "Vessel", bold: true, fontSize: 8, fillColor: GRAY_BG },
        ],
        ...rows,
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
    },
  };
}

// --- Main export ---

export async function exportDprPdf({
  project,
  dpr,
  events,
  detailedLog,
  weather,
  hsseq,
  personnel,
  tools,
}) {
  const pdfMake = (await import("pdfmake/build/pdfmake")).default;
  const pdfFonts = (await import("pdfmake/build/vfs_fonts")).default;
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;

  // Try to load logo
  let logoBase64 = null;
  if (project.logoUrl) {
    logoBase64 = await imageToBase64(project.logoUrl);
  }
  if (!logoBase64) {
    // Fallback to bundled logo
    logoBase64 = await imageToBase64("/logo-delmar_blue.png");
  }

  const mergedLog = mergeEventLog(events, detailedLog);

  const docDefinition = {
    pageSize: "LETTER",
    pageMargins: [40, 70, 40, 50],

    header: (currentPage, pageCount) => ({
      margin: [40, 15, 40, 0],
      columns: [
        {
          width: "*",
          stack: [
            { text: "DELMAR SYSTEMS", fontSize: 10, bold: true, color: NAVY },
            { text: "Daily Progress Report", fontSize: 8, color: NAVY },
          ],
        },
        {
          width: 200,
          canvas: [
            {
              type: "rect",
              x: 0,
              y: 0,
              w: 200,
              h: 30,
              r: 0,
              color: ORANGE,
            },
          ],
        },
        ...(logoBase64
          ? [
              {
                width: 80,
                image: logoBase64,
                fit: [80, 35],
                alignment: "right",
              },
            ]
          : []),
      ],
    }),

    footer: (currentPage, pageCount) => ({
      margin: [40, 10, 40, 0],
      columns: [
        {
          width: "*",
          stack: [
            { text: `Doc No.    ${DOC_NO}`, fontSize: 7, color: "#666666" },
            { text: "delmarvryhof.com", fontSize: 7, color: "#666666" },
          ],
        },
        {
          width: "auto",
          stack: [
            {
              text: `Page ${currentPage} of ${pageCount}`,
              fontSize: 7,
              alignment: "right",
            },
            {
              text: "TOTAL MOORING SOLUTIONS",
              fontSize: 7,
              bold: true,
              alignment: "right",
              color: ORANGE,
            },
          ],
        },
      ],
    }),

    content: [
      buildProjectInfoTable(project, dpr),
      { text: "", margin: [0, 6] },
      buildSummaryTable(dpr),
      { text: "", margin: [0, 6] },
      buildWeatherTable(weather || []),
      { text: "", margin: [0, 4] },
      buildHsseqTable(hsseq || []),
      { text: "", margin: [0, 6] },
      buildEventLogTable(mergedLog),
      { text: "", margin: [0, 8] },
      buildPersonnelTable(personnel || []),
      { text: "", margin: [0, 6] },
      buildToolTable(tools || []),
    ],

    defaultStyle: {
      font: "Roboto",
    },
  };

  const dprNum = String(dpr.dprNumber).padStart(2, "0");
  const filename = `DPR_${dprNum}_${sanitize(project.title)}_${dpr.reportDate}.pdf`;
  pdfMake.createPdf(docDefinition).download(filename);
}
